/**
 * The console's adapter onto the PHONE's assignee logic.
 *
 * ## Why an adapter rather than an implementation
 *
 * Three decisions govern the assignee flow, and all three were settled on
 * mobile long before the console had a picker at all:
 *
 *  1. **Which affordance is active** — a locked bubble, the ghost face-stack,
 *     or an empty field (`assigneeDisplay`). This one has a scar: three
 *     surfaces each inferred it from their own combination of state, list
 *     length and raw text, and disagreed, so the review cards suppressed
 *     prose the detail views locked a pill around. It was fixed by making
 *     ONE function the answer. A console that re-derived it would be the
 *     fourth surface, and the bug would be back.
 *  2. **What the bars read** — `assigneeDistributionPercentages`, which
 *     displays calibrated confidences as-is, normalizes only legacy raw
 *     scores that sum past 100, and refuses to inflate a dampened sub-100
 *     total back up so two weak "John" matches stay at 13%/12% instead of
 *     being re-normalized into a confident 50/50.
 *  3. **What the banner says** — `assignToBannerCopy`, which exists so the
 *     raw resolver enum (`single_vendor_user`) never reaches a human.
 *
 * So this module imports all three and does one job: turn the console's
 * {@link ConsoleAssigneeSuggestion} — the trimmed, wire-sized shape the
 * server projects — back into the resolution those functions read.
 *
 * ## The one console-specific input
 *
 * `procoreConnected`. On the phone it is a fact about the project; here it is
 * derived from the suggestion's PRESENCE. A resolution only exists because a
 * directory was searched, so no suggestion means no directory — and free text
 * genuinely IS the assignee, which is the case that must still lock the
 * field. Passing a blanket `true` would quietly unlock every assignee on
 * every project with no Procore link.
 */

import type { ProcoreAssigneeResolution } from "../../../../backend/src/shared/procoreDailyLog";
import { assigneeDistributionPercentages } from "../../../../backend/src/frontend/components/shared/assignee/assigneeDistribution";
import {
  assigneeDisplay,
  assignToBannerCopy,
  type AssigneeDisplayMode,
} from "../../../../backend/src/frontend/components/shared/assignee/assigneeReviewDisplay";
import type { ConsoleAssigneeCandidate, ConsoleAssigneeSuggestion } from "../api";
import type { ItemEditDirectoryOption } from "../api";
import { preferWorkspaceCandidate } from "../../../../backend/src/frontend/components/shared/assignee/assigneeIdentity";

export type { AssigneeDisplayMode };

export function preferWorkspaceSuggestion(
  suggestion: ConsoleAssigneeSuggestion | undefined,
  directory: ItemEditDirectoryOption[],
): ConsoleAssigneeSuggestion | undefined {
  if (!suggestion) return undefined;
  const selected = suggestion.selected ? preferWorkspaceCandidate(suggestion.selected, directory) : undefined;
  return {
    ...suggestion,
    selected,
    selectedRemoteId: selected?.remoteId ?? suggestion.selectedRemoteId,
    candidates: suggestion.candidates.map(candidate => preferWorkspaceCandidate(candidate, directory))
      .filter((candidate, index, all) => all.findIndex(other => other.remoteId === candidate.remoteId) === index),
  };
}

/**
 * The suggestion, in the shape the phone's helpers read.
 *
 * `remoteId` is already collapsed server-side to the single id a pick
 * commits, so it is written into both id slots the resolver compares
 * against — otherwise a selection made on the console would match the stored
 * resolution by neither name.
 */
function asResolution(
  suggestion: ConsoleAssigneeSuggestion | undefined,
): ProcoreAssigneeResolution | undefined {
  if (!suggestion) return undefined;
  const candidate = (one: ConsoleAssigneeCandidate) => ({
    remoteId: one.remoteId,
    projectUserRemoteId: one.remoteId,
    name: one.name,
    source: one.source,
    ...(one.jobTitle ? { jobTitle: one.jobTitle } : {}),
    ...(one.company ? { company: one.company } : {}),
    ...(one.vendorLabel ? { vendorLabel: one.vendorLabel } : {}),
    ...(typeof one.score === "number" ? { score: one.score } : {}),
    ...(typeof one.confidencePercent === "number"
      ? { confidencePercent: one.confidencePercent }
      : {}),
  });
  return {
    ...(suggestion.state
      ? { state: suggestion.state as ProcoreAssigneeResolution["state"] }
      : {}),
    status: suggestion.status,
    ...(suggestion.selected ? { selectedCandidate: candidate(suggestion.selected) } : {}),
    ...(suggestion.selectedRemoteId
      ? { selectedAssigneeRemoteId: suggestion.selectedRemoteId }
      : {}),
    topCandidates: suggestion.candidates.map(candidate),
    ...(suggestion.vendor
      ? {
          matchedVendor: {
            remoteId: "",
            label: suggestion.vendor.label,
            ...(suggestion.vendor.tradeName
              ? { tradeName: suggestion.vendor.tradeName }
              : {}),
          },
        }
      : {}),
  };
}

/**
 * Which affordance the assignee field shows — `assigneeDisplay`'s answer, for
 * the console's inputs.
 *
 * `value` is the row's already-RESOLVED assignee name, which is what the
 * console has where the phone has the raw stored text. The two agree on the
 * only thing this decision reads from it: whether it is empty. A record whose
 * spoken assignee was "Architect" carries that string in `value` and must
 * still show face-cards rather than lock a pill around a job title — which is
 * exactly the case `procoreConnected` and the candidate list decide, not the
 * text.
 */
export function assigneeMode(opts: {
  suggestion?: ConsoleAssigneeSuggestion;
  value: string;
  /** A pick made in this editing session — see `AssigneePicker`. `undefined`
   *  defers to the record; `""` is an explicit clear. */
  selectedRemoteId?: string;
}): AssigneeDisplayMode {
  return assigneeDisplay({
    resolution: asResolution(opts.suggestion),
    assignee: opts.value,
    selectedRemoteId:
      opts.selectedRemoteId ?? opts.suggestion?.selectedRemoteId ?? null,
    procoreConnected: !!opts.suggestion,
  }).mode;
}

/** The ranked candidates paired with the bar each one draws. Empty when there
 *  is nothing to rank. */
export function rankedCandidates(
  suggestion: ConsoleAssigneeSuggestion | undefined,
): Array<{ candidate: ConsoleAssigneeCandidate; pct: number; lead: boolean }> {
  const candidates = suggestion?.candidates ?? [];
  const shares = assigneeDistributionPercentages(candidates);
  return candidates.map((candidate, index) => ({
    candidate,
    pct: shares[index]?.pct ?? 0,
    lead: shares[index]?.lead ?? false,
  }));
}

/** The Assign-to banner's two lines. */
export function bannerCopy(
  suggestion: ConsoleAssigneeSuggestion | undefined,
): { title: string; sub: string } {
  return assignToBannerCopy(asResolution(suggestion) ?? null);
}
